import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const submissionId = searchParams.get('submission');
  const contractId = searchParams.get('contract'); // Pour édition
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [isEditing, setIsEditing] = useState(false); // Mode édition
  
  const [settings, setSettings] = useState(null); // Pour les inclusions standard et options
  
  const [formData, setFormData] = useState({
    customer: {
      name: '',
      email: '',
      phone: '',
      address: ''
    },
    project: {
      project_type: '',
      model: '',
      width: 0,
      length: 0,
      surface: 0,
      price_per_sqft: 0,
      options: [],
      options_with_prices: [], // NOUVEAU: Options avec prix
      start_date: ''
    },
    standard_inclusions: [], // NOUVEAU: Inclusions standard modifiables
    custom_inclusions: [], // NOUVEAU: Inclusions personnalisées (renommé de inclusions)
    inclusions: [], // DEPRECATED: garder pour compatibilité
    calculation: {
      surface: 0,
      price_per_sqft: 0,
      subtotal: 0,
      options_total: 0,
      tps: 0,
      tvq: 0,
      total_ttc: 0
    },
    payment_schedule: {
      payment_1_percent: 30,
      payment_2_percent: 30,
      payment_3_percent: 30,
      payment_4_percent: 10
    },
    project_schedule: {
      milestone_1: 'Signature du contrat',
      milestone_1_date: '',
      milestone_2: 'Début des travaux',
      milestone_2_date: '',
      milestone_3: 'Milieu des travaux et contrôle qualité',
      milestone_3_date: '',
      milestone_4: 'Fin des travaux, finition et livraison',
      milestone_4_date: ''
    },
    payment_method: 'Financement bancaire',
    special_conditions: '',
    contract_date: new Date().toISOString().split('T')[0]
  });

  // Fonction pour recalculer automatiquement les montants (garde payment_schedule et calendar existants)
  const recalculateAmounts = (surface, pricePerSqft, optionsTotal, existingCalc = null) => {
    const subtotal = surface * pricePerSqft;
    const totalBeforeTax = subtotal + optionsTotal;
    const tps = totalBeforeTax * 0.05; // 5%
    const tvq = totalBeforeTax * 0.09975; // 9.975%
    const totalTTC = totalBeforeTax + tps + tvq;
    
    return {
      surface: parseFloat(surface) || 0,
      price_per_sqft: parseFloat(pricePerSqft) || 0,
      subtotal: Math.round(subtotal * 100) / 100,
      options_total: parseFloat(optionsTotal) || 0,
      tps: Math.round(tps * 100) / 100,
      tvq: Math.round(tvq * 100) / 100,
      total_ttc: Math.round(totalTTC * 100) / 100,
      payment_schedule: existingCalc?.payment_schedule || {},
      calendar: existingCalc?.calendar || {}
    };
  };

  // Fonction pour recalculer le total des options depuis options_with_prices
  const recalculateOptionsTotal = (optionsWithPrices) => {
    if (!optionsWithPrices || optionsWithPrices.length === 0) return 0;
    return optionsWithPrices.reduce((sum, opt) => sum + (parseFloat(opt.price) || 0), 0);
  };

  // Charger les settings au montage pour avoir les inclusions standard et les options
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Erreur chargement settings:', error);
        // Même en cas d'erreur, on définit settings à un objet vide pour débloquer le chargement
        setSettings({ options: [], project_type_inclusions: [] });
      }
    };
    loadSettings();
  }, []);

  // Charger le contrat ou la soumission une fois que les settings sont chargés
  useEffect(() => {
    if (!settings) {
      // Attendre que les settings soient chargés
      return;
    }

    if (contractId) {
      // Mode édition
      setIsEditing(true);
      fetchContract();
    } else if (submissionId) {
      // Mode création depuis soumission
      setIsEditing(false);
      fetchSubmission();
    } else {
      toast.error('ID de soumission ou contrat manquant');
      navigate('/admin/contracts');
    }
  }, [settings, submissionId, contractId]);

  const fetchContract = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const contract = response.data;
      
      // Gérer les options avec prix du contrat
      let optionsWithPrices = [];
      if (contract.project.options_with_prices && contract.project.options_with_prices.length > 0) {
        // Le contrat a déjà options_with_prices (nouveau format)
        optionsWithPrices = contract.project.options_with_prices;
      } else if (contract.project.options && contract.project.options.length > 0 && settings) {
        // Ancien contrat avec options textuelles - convertir en options avec prix
        optionsWithPrices = contract.project.options.map(optName => {
          const optionInSettings = settings.options?.find(opt => 
            opt.name === optName || opt.label === optName
          );
          return {
            name: optName,
            price: optionInSettings ? optionInSettings.price : 0
          };
        });
      }
      
      // Gérer les inclusions standard du contrat
      let standardInclusions = [];
      if (contract.standard_inclusions && contract.standard_inclusions.length > 0) {
        // Le contrat a déjà standard_inclusions
        standardInclusions = contract.standard_inclusions;
      } else if (settings) {
        // Ancien contrat sans standard_inclusions - charger depuis settings
        const projectTypeInc = settings.project_type_inclusions?.find(
          pti => pti.project_type === contract.project.project_type
        );
        if (projectTypeInc) {
          standardInclusions = projectTypeInc.inclusions.map(inc => ({
            category: inc.category,
            items: Array.isArray(inc.items) ? inc.items.join(', ') : inc.items,
            is_standard: true
          }));
        }
      }
      
      // Pré-remplir le formulaire avec les données du contrat existant
      setFormData({
        customer: contract.customer,
        project: {
          ...contract.project,
          options_with_prices: optionsWithPrices // Utiliser les options avec prix
        },
        calculation: contract.calculation || recalculateAmounts(
          contract.project.surface,
          contract.project.price_per_sqft || 0,
          recalculateOptionsTotal(optionsWithPrices)
        ),
        standard_inclusions: standardInclusions, // Inclusions standard du contrat
        custom_inclusions: contract.custom_inclusions || contract.inclusions || [],
        inclusions: contract.inclusions || [], // Compatibilité
        payment_schedule: contract.payment_schedule,
        project_schedule: contract.project_schedule,
        payment_method: contract.payment_method,
        special_conditions: contract.special_conditions || '',
        contract_date: contract.contract_date
      });
      
      // Créer un objet submission pour l'affichage des infos
      setSubmission({
        customer: contract.customer,
        project: contract.project,
        calculation: contract.calculation
      });
      
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement du contrat');
      navigate('/admin/contracts');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmission = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const sub = response.data;
      setSubmission(sub);
      
      // Calculer les dates prévisionnelles (environ 3 mois de délai)
      const today = new Date();
      const contractDate = today.toISOString().split('T')[0];
      
      // Dates des jalons basées sur le calendrier du précontrat s'il existe
      const calendar = sub.calculation?.calendar || {};
      
      // Charger les inclusions standard depuis settings selon le type de projet
      let standardInclusions = [];
      if (settings) {
        const projectTypeInc = settings.project_type_inclusions?.find(
          pti => pti.project_type === sub.project.project_type
        );
        if (projectTypeInc) {
          standardInclusions = projectTypeInc.inclusions.map(inc => ({
            category: inc.category,
            items: Array.isArray(inc.items) ? inc.items.join(', ') : inc.items,
            is_standard: true
          }));
        }
      }
      
      // Convertir les options textuelles en options avec prix
      let optionsWithPrices = [];
      if (sub.project.options && sub.project.options.length > 0 && settings) {
        optionsWithPrices = sub.project.options.map(optName => {
          // Chercher le prix dans settings.options
          const optionInSettings = settings.options?.find(opt => 
            opt.name === optName || opt.label === optName
          );
          return {
            name: optName,
            price: optionInSettings ? optionInSettings.price : 0
          };
        });
      }
      
      // Recalculer le total des options
      const optionsTotal = recalculateOptionsTotal(optionsWithPrices);
      
      // Pré-remplir TOUS les champs du formulaire avec les données de la soumission
      setFormData({
        ...formData,
        customer: sub.customer,
        project: {
          ...sub.project,
          start_date: calendar.start || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +14 jours
          surface: sub.calculation?.surface || sub.project.surface || 0,
          width: sub.project.width || 0,
          length: sub.project.length || 0,
          options_with_prices: optionsWithPrices // NOUVEAU
        },
        standard_inclusions: standardInclusions, // NOUVEAU
        custom_inclusions: [], // Vide au début
        calculation: recalculateAmounts(
          sub.calculation?.surface || 0,
          sub.calculation?.price_per_sqft || 0,
          optionsTotal
        ),
        payment_schedule: {
          payment_1_percent: 5,  // Dépôt initial
          payment_1_amount: 0,   // Sera calculé automatiquement
          payment_2_percent: 45, // À la signature
          payment_2_amount: 0,
          payment_3_percent: 30, // Mi-production
          payment_3_amount: 0,
          payment_4_percent: 20, // Livraison
          payment_4_amount: 0
        },
        project_schedule: {
          milestone_1: 'Signature du contrat',
          milestone_1_date: calendar.validation || contractDate,
          milestone_2: 'Début des travaux',
          milestone_2_date: calendar.start || new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          milestone_3: 'Milieu des travaux et contrôle qualité',
          milestone_3_date: calendar.middle || new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          milestone_4: 'Fin des travaux, finition et livraison',
          milestone_4_date: calendar.delivery || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        },
        payment_method: 'Financement bancaire',
        special_conditions: '',
        contract_date: contractDate
      });
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement de la soumission');
      navigate('/admin/contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation des pourcentages de paiement
    const totalPercent = 
      formData.payment_schedule.payment_1_percent +
      formData.payment_schedule.payment_2_percent +
      formData.payment_schedule.payment_3_percent +
      formData.payment_schedule.payment_4_percent;
    
    if (totalPercent !== 100) {
      toast.error(`Le total des pourcentages doit être égal à 100% (actuellement: ${totalPercent}%)`);
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      const contractData = {
        submission_id: submissionId || (isEditing ? contractId : ''),
        customer: formData.customer,
        project: formData.project,
        calculation: formData.calculation, // NOUVEAU: envoyer le calcul mis à jour
        standard_inclusions: formData.standard_inclusions, // NOUVEAU
        custom_inclusions: formData.custom_inclusions, // NOUVEAU
        payment_schedule: formData.payment_schedule,
        project_schedule: formData.project_schedule,
        payment_method: formData.payment_method,
        special_conditions: formData.special_conditions,
        contract_date: formData.contract_date
      };
      
      if (isEditing && contractId) {
        // Mode édition - PATCH
        await axios.patch(`${API}/contracts/${contractId}`, contractData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contrat modifié avec succès!');
      } else {
        // Mode création - POST
        await axios.post(`${API}/contracts`, contractData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contrat créé avec succès!');
      }
      
      navigate('/admin/contracts');
    } catch (error) {
      console.error(error);
      toast.error(isEditing ? 'Erreur lors de la modification du contrat' : 'Erreur lors de la création du contrat');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5]">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            {/* Spinner amélioré */}
            <div className="relative mx-auto w-16 h-16 mb-4">
              <div className="absolute top-0 left-0 w-full h-full border-4 border-gray-200 rounded-full"></div>
              <div className="absolute top-0 left-0 w-full h-full border-4 border-[#335C2E] border-t-transparent rounded-full animate-spin"></div>
            </div>
            
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#335C2E' }}>
              {contractId ? 'Chargement du contrat' : 'Chargement de la soumission'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {contractId ? 'Récupération des informations du contrat...' : 'Récupération des informations du client...'}
            </p>
            
            {/* Barre de progression améliorée */}
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner">
              <div 
                className="h-full bg-gradient-to-r from-[#335C2E] to-[#C7A027] transition-all duration-500 ease-out animate-pulse"
                style={{ width: '70%' }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-2">70% complété</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/admin/contracts')}
            className="p-2 hover:bg-gray-200 rounded"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-bold" style={{ color: '#335C2E' }}>
            {isEditing ? 'Modifier le contrat de vente' : 'Créer un contrat de vente'}
          </h1>
        </div>

        {/* Barre de progression améliorée */}
        {saving && (
          <div className="mb-4 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg shadow-md">
            <div className="flex items-center gap-3 mb-3">
              <div className="relative w-8 h-8">
                <div className="absolute top-0 left-0 w-full h-full border-3 border-gray-300 rounded-full"></div>
                <div className="absolute top-0 left-0 w-full h-full border-3 border-[#C7A027] border-t-transparent rounded-full animate-spin"></div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Création du contrat en cours...</p>
                <p className="text-xs text-gray-600">Génération du document PDF et sauvegarde</p>
              </div>
            </div>
            
            {/* Barre de progression avec animation */}
            <div className="w-full h-4 bg-gray-300 rounded-full overflow-hidden shadow-inner">
              <div 
                className="h-full bg-gradient-to-r from-[#C7A027] via-[#D4AF37] to-[#FFD700] transition-all duration-1000 ease-in-out"
                style={{ 
                  width: '85%',
                  animation: 'progressPulse 1.5s ease-in-out infinite'
                }}
              ></div>
            </div>
            <p className="text-xs text-gray-600 mt-2 text-center">85% - Finalisation...</p>
            
            {/* Message d'information */}
            <div className="mt-3 p-2 bg-white/50 rounded text-xs text-gray-700 border-l-4 border-[#C7A027]">
              <span className="font-semibold">💡 Info:</span> La génération peut prendre quelques secondes selon la complexité du contrat.
            </div>
          </div>
        )}
        
        {/* Animation CSS personnalisée */}
        <style>{`
          @keyframes progressPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}</style>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations client */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
              1. Informations client
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nom complet *</label>
                <input
                  type="text"
                  value={formData.customer.name}
                  onChange={(e) => setFormData({
                    ...formData,
                    customer: { ...formData.customer, name: e.target.value }
                  })}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.customer.email}
                  onChange={(e) => setFormData({
                    ...formData,
                    customer: { ...formData.customer, email: e.target.value }
                  })}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Téléphone *</label>
                <input
                  type="tel"
                  value={formData.customer.phone}
                  onChange={(e) => setFormData({
                    ...formData,
                    customer: { ...formData.customer, phone: e.target.value }
                  })}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Adresse *</label>
                <input
                  type="text"
                  value={formData.customer.address}
                  onChange={(e) => setFormData({
                    ...formData,
                    customer: { ...formData.customer, address: e.target.value }
                  })}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
            </div>
          </div>

          {/* Détails du projet */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
              2. Détails du projet
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Type de projet *</label>
                <input
                  type="text"
                  value={formData.project.project_type}
                  onChange={(e) => setFormData({
                    ...formData,
                    project: { ...formData.project, project_type: e.target.value }
                  })}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Modèle *</label>
                <input
                  type="text"
                  value={formData.project.model}
                  onChange={(e) => setFormData({
                    ...formData,
                    project: { ...formData.project, model: e.target.value }
                  })}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Largeur (pieds) *</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.project.width}
                  onChange={(e) => {
                    const width = parseFloat(e.target.value) || 0;
                    const length = formData.project.length || 0;
                    const newSurface = width * length;
                    const optionsTotal = recalculateOptionsTotal(formData.project.options_with_prices);
                    const pricePerSqft = formData.calculation.price_per_sqft || formData.project.price_per_sqft || 0;
                    const newCalculation = recalculateAmounts(newSurface, pricePerSqft, optionsTotal, formData.calculation);
                    setFormData({
                      ...formData,
                      project: { ...formData.project, width, surface: newSurface },
                      calculation: newCalculation
                    });
                  }}
                  className="w-full p-2 border rounded"
                  required
                  data-testid="project-width"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Longueur (pieds) *</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.project.length}
                  onChange={(e) => {
                    const length = parseFloat(e.target.value) || 0;
                    const width = formData.project.width || 0;
                    const newSurface = width * length;
                    const optionsTotal = recalculateOptionsTotal(formData.project.options_with_prices);
                    const pricePerSqft = formData.calculation.price_per_sqft || formData.project.price_per_sqft || 0;
                    const newCalculation = recalculateAmounts(newSurface, pricePerSqft, optionsTotal, formData.calculation);
                    setFormData({
                      ...formData,
                      project: { ...formData.project, length, surface: newSurface },
                      calculation: newCalculation
                    });
                  }}
                  className="w-full p-2 border rounded"
                  required
                  data-testid="project-length"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Surface (pi²)</label>
                <input
                  type="number"
                  value={formData.project.surface}
                  className="w-full p-2 border rounded bg-gray-100"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Prix par pi² *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.project.price_per_sqft}
                  onChange={(e) => setFormData({
                    ...formData,
                    project: { ...formData.project, price_per_sqft: parseFloat(e.target.value) }
                  })}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
            </div>

            {/* Options avec prix - MODIFIABLES */}
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#335C2E' }}>
                <span>✅</span> Options avec prix (modifiable)
              </h3>
              
              {/* Liste des options avec prix modifiables */}
              <div className="space-y-2 mb-3">
                {formData.project.options_with_prices && formData.project.options_with_prices.length > 0 ? (
                  formData.project.options_with_prices.map((option, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center gap-2 p-2 bg-white rounded border border-green-300"
                    >
                      <input
                        type="text"
                        value={option.name}
                        onChange={(e) => {
                          const newOptions = [...formData.project.options_with_prices];
                          newOptions[idx].name = e.target.value;
                          setFormData({
                            ...formData,
                            project: { ...formData.project, options_with_prices: newOptions }
                          });
                        }}
                        className="flex-1 p-2 text-sm border rounded"
                        placeholder="Nom de l'option"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={option.price}
                        onChange={(e) => {
                          const newOptions = [...formData.project.options_with_prices];
                          newOptions[idx].price = parseFloat(e.target.value) || 0;
                          const newOptionsTotal = recalculateOptionsTotal(newOptions);
                          const newCalculation = recalculateAmounts(
                            formData.calculation.surface,
                            formData.calculation.price_per_sqft,
                            newOptionsTotal,
                            formData.calculation
                          );
                          setFormData({
                            ...formData,
                            project: { ...formData.project, options_with_prices: newOptions },
                            calculation: newCalculation
                          });
                        }}
                        className="w-32 p-2 text-sm border rounded"
                        placeholder="Prix ($)"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newOptions = formData.project.options_with_prices.filter((_, i) => i !== idx);
                          const newOptionsTotal = recalculateOptionsTotal(newOptions);
                          const newCalculation = recalculateAmounts(
                            formData.calculation.surface,
                            formData.calculation.price_per_sqft,
                            newOptionsTotal,
                            formData.calculation
                          );
                          setFormData({
                            ...formData,
                            project: { ...formData.project, options_with_prices: newOptions },
                            calculation: newCalculation
                          });
                        }}
                        className="px-2 py-1 text-xs text-white rounded hover:opacity-80"
                        style={{ background: '#DC3545' }}
                        title="Supprimer cette option"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">Aucune option avec prix ajoutée</p>
                )}
              </div>
              
              {/* Ajouter une nouvelle option avec prix */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Nom de l'option..."
                  className="flex-1 p-2 text-sm border rounded"
                  id="new-option-name-input"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Prix ($)"
                  className="w-32 p-2 text-sm border rounded"
                  id="new-option-price-input"
                />
                <button
                  type="button"
                  onClick={() => {
                    const nameInput = document.getElementById('new-option-name-input');
                    const priceInput = document.getElementById('new-option-price-input');
                    const newName = nameInput.value.trim();
                    const newPrice = parseFloat(priceInput.value) || 0;
                    
                    if (newName) {
                      const currentOptions = formData.project.options_with_prices || [];
                      const newOptions = [...currentOptions, { name: newName, price: newPrice }];
                      const newOptionsTotal = recalculateOptionsTotal(newOptions);
                      const newCalculation = recalculateAmounts(
                        formData.calculation.surface,
                        formData.calculation.price_per_sqft,
                        newOptionsTotal,
                        formData.calculation
                      );
                      setFormData({
                        ...formData,
                        project: { ...formData.project, options_with_prices: newOptions },
                        calculation: newCalculation
                      });
                      nameInput.value = '';
                      priceInput.value = '';
                    }
                  }}
                  className="px-4 py-2 text-sm text-white rounded hover:opacity-90"
                  style={{ background: '#335C2E' }}
                >
                  + Ajouter
                </button>
              </div>
              
              <p className="text-xs text-gray-600 mt-2">
                💡 Le total des options est calculé automatiquement et mis à jour dans le résumé financier
              </p>
            </div>

            {/* Inclusions standard - MODIFIABLES */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#1976D2' }}>
                <span>📄</span> Inclusions standard (modifiable pour ce contrat)
              </h3>
              
              <p className="text-xs text-gray-600 mb-3">
                Inclusions standard du type de projet. Vous pouvez les modifier ou les supprimer pour ce contrat spécifique.
              </p>
              
              {/* Liste des inclusions standard modifiables */}
              <div className="space-y-2 mb-3">
                {formData.standard_inclusions && formData.standard_inclusions.length > 0 ? (
                  formData.standard_inclusions.map((inclusion, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-start gap-2 p-2 bg-white rounded border border-blue-300"
                    >
                      <div className="flex-1 space-y-1">
                        <input
                          type="text"
                          value={inclusion.category}
                          onChange={(e) => {
                            const newInclusions = [...formData.standard_inclusions];
                            newInclusions[idx].category = e.target.value;
                            setFormData({
                              ...formData,
                              standard_inclusions: newInclusions
                            });
                          }}
                          className="w-full p-1 text-sm font-semibold border rounded"
                          style={{ color: '#1976D2' }}
                          placeholder="Catégorie"
                        />
                        <input
                          type="text"
                          value={inclusion.items}
                          onChange={(e) => {
                            const newInclusions = [...formData.standard_inclusions];
                            newInclusions[idx].items = e.target.value;
                            setFormData({
                              ...formData,
                              standard_inclusions: newInclusions
                            });
                          }}
                          className="w-full p-1 text-xs border rounded text-gray-600"
                          placeholder="Items (séparés par des virgules)"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newInclusions = formData.standard_inclusions.filter((_, i) => i !== idx);
                          setFormData({
                            ...formData,
                            standard_inclusions: newInclusions
                          });
                        }}
                        className="px-2 py-1 text-xs text-white rounded hover:opacity-80"
                        style={{ background: '#DC3545' }}
                        title="Supprimer cette inclusion standard"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">Aucune inclusion standard configurée pour ce type de projet</p>
                )}
              </div>
              
              <p className="text-xs text-gray-600 mt-2">
                💡 Ces modifications ne s'appliquent qu'à ce contrat. Les inclusions standard globales restent inchangées.
              </p>
            </div>

            {/* Inclusions personnalisées - MODIFIABLES */}
            <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#6B21A8' }}>
                <span>📋</span> Inclusions personnalisées (modifiable)
              </h3>
              
              <p className="text-xs text-gray-600 mb-3">
                Ajoutez des inclusions spécifiques à ce contrat (en plus des inclusions standards)
              </p>
              
              {/* Liste des inclusions personnalisées avec bouton supprimer */}
              <div className="space-y-2 mb-3">
                {formData.custom_inclusions && formData.custom_inclusions.length > 0 ? (
                  formData.custom_inclusions.map((inclusion, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-start gap-2 p-2 bg-white rounded border border-purple-300"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-purple-700">{inclusion.category}</p>
                        <p className="text-xs text-gray-600">{inclusion.items}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newInclusions = formData.custom_inclusions.filter((_, i) => i !== idx);
                          setFormData({
                            ...formData,
                            custom_inclusions: newInclusions
                          });
                        }}
                        className="px-2 py-1 text-xs text-white rounded hover:opacity-80"
                        style={{ background: '#DC3545' }}
                        title="Supprimer cette inclusion"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">Aucune inclusion personnalisée ajoutée</p>
                )}
              </div>
              
              {/* Ajouter une nouvelle inclusion */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Catégorie (ex: Électricité supplémentaire)"
                    className="flex-1 p-2 text-sm border rounded"
                    id="new-custom-inclusion-category"
                  />
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Description (ex: Prises USB, Éclairage LED intelligent)"
                    className="flex-1 p-2 text-sm border rounded"
                    id="new-custom-inclusion-items"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const category = document.getElementById('new-custom-inclusion-category').value.trim();
                        const items = e.target.value.trim();
                        if (category && items) {
                          const currentInclusions = formData.custom_inclusions || [];
                          setFormData({
                            ...formData,
                            custom_inclusions: [...currentInclusions, { category, items, is_standard: false }]
                          });
                          document.getElementById('new-custom-inclusion-category').value = '';
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const category = document.getElementById('new-custom-inclusion-category').value.trim();
                      const items = document.getElementById('new-custom-inclusion-items').value.trim();
                      if (category && items) {
                        const currentInclusions = formData.custom_inclusions || [];
                        setFormData({
                          ...formData,
                          custom_inclusions: [...currentInclusions, { category, items, is_standard: false }]
                        });
                        document.getElementById('new-custom-inclusion-category').value = '';
                        document.getElementById('new-custom-inclusion-items').value = '';
                      }
                    }}
                    className="px-4 py-2 text-sm text-white rounded hover:opacity-90"
                    style={{ background: '#6B21A8' }}
                  >
                    + Ajouter
                  </button>
                </div>
              </div>
              
              <p className="text-xs text-gray-600 mt-2">
                💡 Les inclusions standards sont modifiables ci-dessus. Celles-ci sont des ajouts personnalisés supplémentaires.
              </p>
            </div>

            {/* Résumé financier ÉDITABLE */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#1976D2' }}>
                <span>💰</span> Résumé financier (modifiable)
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Surface (pi²)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.calculation.surface}
                    onChange={(e) => {
                      const newSurface = parseFloat(e.target.value) || 0;
                      const newCalculation = recalculateAmounts(
                        newSurface,
                        formData.calculation.price_per_sqft,
                        formData.calculation.options_total,
                        formData.calculation
                      );
                      setFormData({
                        ...formData,
                        calculation: newCalculation,
                        project: { ...formData.project, surface: newSurface }
                      });
                    }}
                    className="w-full p-2 border rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Prix/pi² ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.calculation.price_per_sqft}
                    onChange={(e) => {
                      const newPrice = parseFloat(e.target.value) || 0;
                      const newCalculation = recalculateAmounts(
                        formData.calculation.surface,
                        newPrice,
                        formData.calculation.options_total,
                        formData.calculation
                      );
                      setFormData({
                        ...formData,
                        calculation: newCalculation,
                        project: { ...formData.project, price_per_sqft: newPrice }
                      });
                    }}
                    className="w-full p-2 border rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Sous-total ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.calculation.subtotal}
                    readOnly
                    className="w-full p-2 border rounded bg-gray-100"
                    title="Calculé automatiquement: Surface × Prix/pi²"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Options - Total ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.calculation.options_total}
                    readOnly
                    className="w-full p-2 border rounded bg-gray-100"
                    title="Calculé automatiquement depuis la somme des prix des options"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Somme automatique des prix des options ci-dessus
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">TPS (5%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.calculation.tps}
                    readOnly
                    className="w-full p-2 border rounded bg-gray-100"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">TVQ (9.975%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.calculation.tvq}
                    readOnly
                    className="w-full p-2 border rounded bg-gray-100"
                  />
                </div>
              </div>
              
              <div className="pt-3 border-t border-blue-300">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Total TTC:</span>
                  <span className="font-bold text-xl" style={{ color: '#C7A027' }}>
                    {formData.calculation.total_ttc.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $
                  </span>
                </div>
              </div>
              
              <p className="text-xs text-gray-600 mt-2">
                💡 Les taxes (TPS/TVQ) et le total TTC sont calculés automatiquement.
              </p>
            </div>
          </div>

          {/* Calendrier de paiement */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
              3. Calendrier de paiement
            </h2>
            {(() => {
              const totalTTC = formData.calculation.total_ttc || 0;
              const paymentLabels = [
                { key: 'payment_1_percent', label: '1. Signature du contrat' },
                { key: 'payment_2_percent', label: '2. Début des travaux' },
                { key: 'payment_3_percent', label: '3. Milieu des travaux et contrôle qualité' },
                { key: 'payment_4_percent', label: '4. Fin des travaux, finition et livraison' },
              ];
              const totalPercent = paymentLabels.reduce((sum, p) => sum + (formData.payment_schedule[p.key] || 0), 0);
              return (
                <div className="space-y-3">
                  {/* En-tête tableau */}
                  <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 uppercase px-1">
                    <div className="col-span-5">Échéance</div>
                    <div className="col-span-3">Pourcentage</div>
                    <div className="col-span-4 text-right">Montant calculé</div>
                  </div>
                  {paymentLabels.map((item) => {
                    const pct = formData.payment_schedule[item.key] || 0;
                    const amount = Math.round(totalTTC * pct / 100 * 100) / 100;
                    return (
                      <div key={item.key} className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 rounded border">
                        <div className="col-span-5 text-sm font-medium">{item.label}</div>
                        <div className="col-span-3">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              step="0.1"
                              value={pct}
                              onChange={(e) => setFormData({
                                ...formData,
                                payment_schedule: { ...formData.payment_schedule, [item.key]: parseFloat(e.target.value) || 0 }
                              })}
                              className="w-full p-2 border rounded text-sm"
                              required
                              data-testid={`payment-${item.key}`}
                            />
                            <span className="text-sm text-gray-500">%</span>
                          </div>
                        </div>
                        <div className="col-span-4 text-right text-sm font-semibold" style={{ color: '#C7A027' }}>
                          {amount.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <p className="text-xs text-gray-500">
                      Total: {totalPercent.toFixed(1)}%
                      {totalPercent !== 100 && <span className="text-red-500 ml-2">Doit totaliser 100%</span>}
                    </p>
                    <p className="text-sm font-bold" style={{ color: '#335C2E' }}>
                      {totalTTC.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Calendrier prévisionnel */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
              4. Calendrier prévisionnel des travaux
            </h2>
            <div className="space-y-4">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Jalon {num} *</label>
                    <input
                      type="text"
                      value={formData.project_schedule[`milestone_${num}`]}
                      onChange={(e) => setFormData({
                        ...formData,
                        project_schedule: { 
                          ...formData.project_schedule, 
                          [`milestone_${num}`]: e.target.value 
                        }
                      })}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Date prévue *</label>
                    <input
                      type="date"
                      value={formData.project_schedule[`milestone_${num}_date`]}
                      onChange={(e) => setFormData({
                        ...formData,
                        project_schedule: { 
                          ...formData.project_schedule, 
                          [`milestone_${num}_date`]: e.target.value 
                        }
                      })}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Informations complémentaires */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
              5. Informations complémentaires
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Date du contrat *</label>
                <input
                  type="date"
                  value={formData.contract_date}
                  onChange={(e) => setFormData({ ...formData, contract_date: e.target.value })}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Mode de paiement</label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full p-2 border rounded"
                >
                  <option value="Financement bancaire">Financement bancaire</option>
                  <option value="Comptant">Comptant</option>
                  <option value="Chèque">Chèque</option>
                  <option value="Virement bancaire">Virement bancaire</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Conditions spéciales</label>
                <textarea
                  value={formData.special_conditions}
                  onChange={(e) => setFormData({ ...formData, special_conditions: e.target.value })}
                  className="w-full p-2 border rounded"
                  rows={4}
                  placeholder="Ajoutez des conditions spéciales si nécessaire..."
                />
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-4 justify-end">
            <button
              type="button"
              onClick={() => navigate('/admin/contracts')}
              className="px-6 py-2 border rounded hover:bg-gray-100"
              disabled={saving}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-2 text-white rounded"
              style={{ background: '#335C2E' }}
              disabled={saving}
            >
              <Save size={20} />
              {saving ? (isEditing ? 'Modification en cours...' : 'Création en cours...') : (isEditing ? 'Modifier le contrat' : 'Créer le contrat')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractForm;
